// sim/discoveryData.ts — the EVOLUTION DISCOVERY catalogue: morph names,
// descriptions, synergy requirements (gene types + traits), and prestige tier.
// Pure data, no logic. Indexed positionally by morph id (0..MORPH_COUNT-1).
// The index order is a HARD CONTRACT shared with src/sim/discovery.ts (the
// system) and src/sim/discoveryExample.ts (world builder + CLI).
// Grounded in docs/LORE.md §"Evolution Discovery — emergent morphs".
import { GENE_TYPE, GENE_TRAIT, RARITY } from '../types.ts';
import type { GeneType, GeneTrait, Rarity } from '../types.ts';

// ---------------------------------------------------------------------------
// MORPH_NAME — display name for each emergent morph species.
// Index 0 MUST be "Toxic Bomber" (engine contract).
// ---------------------------------------------------------------------------

export const MORPH_NAME: readonly string[] = [
  'Toxic Bomber',        // 0
  'Sprintdrone',         // 1
  'Bulwark Sentinel',    // 2
  'Venom Lancer',        // 3
  'Phantom Skimmer',     // 4
  'Ironclad Colossus',   // 5
  'Caustic Broodmother', // 6
  'Razorwing Reaper',    // 7
  'Sentinel Augur',      // 8
  'Quicksilver Stalker', // 9
  'Molten Juggernaut',   // 10
  'Mirewing Leviathan',  // 11
];

// ---------------------------------------------------------------------------
// MORPH_DESC — one evocative-but-factual line each, grounded in garden lore.
// Describes what the morph IS, how it arose, and what it means for the Brood.
// ---------------------------------------------------------------------------

export const MORPH_DESC: readonly string[] = [
  // 0 — Toxic Bomber
  'An Enlarged winged form whose swollen venom gland has fused with its flight musculature, releasing clouds of toxin on each wingbeat.',
  // 1 — Sprintdrone
  'A lightly-built runner-flier: stripped Limbs and gossamer Wings let it cross ground faster than any single-caste member.',
  // 2 — Bulwark Sentinel
  'Mandibles thick as patio stone and Reinforced carapace plating that rivals shed-timber bark; this form holds a perimeter, not a lane.',
  // 3 — Venom Lancer
  'A precise striker whose Toxic venom gland feeds directly into a sharpened mandible tip, delivering a lethal dose on a single bite.',
  // 4 — Phantom Skimmer
  'A near-weightless flier whose Acute antennae can read air pressure gradients, ghosting over rival nests unseen before they sense it.',
  // 5 — Ironclad Colossus
  'Oversized and armoured from limb-tip to carapace ridge; Reinforced plating stacks over an Enlarged frame that simply outlasts every attacker.',
  // 6 — Caustic Broodmother
  'A vast Enlarged form whose Toxic and Regenerative venom-carapace weeps acid while healing itself; the garden\'s most feared siege presence.',
  // 7 — Razorwing Reaper
  'Wings honed to a Volatile edge — each pass is a slash; the unstable chemistry detonates on strong impacts, shredding enemies caught in the blast.',
  // 8 — Sentinel Augur
  'Acute antennae mounted on long Limbs let this morph triangulate chemical signals across an entire region, mapping threats before they close.',
  // 9 — Quicksilver Stalker
  'Lightweight Limbs paired with hyper-sensitive Antennae produce a hunter that tracks prey by scent trail and outpaces it by raw leg-speed.',
  // 10 — Molten Juggernaut
  'A three-way convergence of Reinforced carapace, Volatile mandibles, and Enlarged frame; when it bites, the blast reshapes the terrain around it.',
  // 11 — Mirewing Leviathan
  'A Legendary three-system fusion: Enlarged wings bear a venom-laced carapace that Regenerates between strikes, an airborne living fortress.',
];

// ---------------------------------------------------------------------------
// MORPH_REQ_TYPES — ALL listed GeneType values must be present in the colony
// gene pool (at least one gene of each type) for the synergy to resolve.
// ---------------------------------------------------------------------------

export const MORPH_REQ_TYPES: readonly (readonly GeneType[])[] = [
  // 0 — Toxic Bomber
  [GENE_TYPE.Wing, GENE_TYPE.VenomGland],
  // 1 — Sprintdrone
  [GENE_TYPE.Limb, GENE_TYPE.Wing],
  // 2 — Bulwark Sentinel
  [GENE_TYPE.Carapace, GENE_TYPE.Mandible],
  // 3 — Venom Lancer
  [GENE_TYPE.VenomGland, GENE_TYPE.Mandible],
  // 4 — Phantom Skimmer
  [GENE_TYPE.Wing, GENE_TYPE.Antenna],
  // 5 — Ironclad Colossus
  [GENE_TYPE.Carapace, GENE_TYPE.Limb],
  // 6 — Caustic Broodmother
  [GENE_TYPE.VenomGland, GENE_TYPE.Carapace],
  // 7 — Razorwing Reaper
  [GENE_TYPE.Wing, GENE_TYPE.Mandible],
  // 8 — Sentinel Augur
  [GENE_TYPE.Antenna, GENE_TYPE.Limb],
  // 9 — Quicksilver Stalker
  [GENE_TYPE.Limb, GENE_TYPE.Antenna],
  // 10 — Molten Juggernaut
  [GENE_TYPE.Carapace, GENE_TYPE.Mandible, GENE_TYPE.Limb],
  // 11 — Mirewing Leviathan
  [GENE_TYPE.Wing, GENE_TYPE.Carapace, GENE_TYPE.VenomGland],
];

// ---------------------------------------------------------------------------
// MORPH_REQ_TRAITS — ALL listed GeneTrait values must be present (stack >= 1)
// in the colony gene pool for the synergy to resolve.
// ---------------------------------------------------------------------------

export const MORPH_REQ_TRAITS: readonly (readonly GeneTrait[])[] = [
  // 0 — Toxic Bomber
  [GENE_TRAIT.Enlarged],
  // 1 — Sprintdrone
  [GENE_TRAIT.Lightweight],
  // 2 — Bulwark Sentinel
  [GENE_TRAIT.Reinforced],
  // 3 — Venom Lancer
  [GENE_TRAIT.Toxic],
  // 4 — Phantom Skimmer
  [GENE_TRAIT.Lightweight, GENE_TRAIT.Acute],
  // 5 — Ironclad Colossus
  [GENE_TRAIT.Reinforced, GENE_TRAIT.Enlarged],
  // 6 — Caustic Broodmother
  [GENE_TRAIT.Toxic, GENE_TRAIT.Regenerative, GENE_TRAIT.Enlarged],
  // 7 — Razorwing Reaper
  [GENE_TRAIT.Volatile],
  // 8 — Sentinel Augur
  [GENE_TRAIT.Acute],
  // 9 — Quicksilver Stalker
  [GENE_TRAIT.Lightweight, GENE_TRAIT.Acute],
  // 10 — Molten Juggernaut
  [GENE_TRAIT.Reinforced, GENE_TRAIT.Volatile, GENE_TRAIT.Enlarged],
  // 11 — Mirewing Leviathan
  [GENE_TRAIT.Enlarged, GENE_TRAIT.Regenerative],
];

// ---------------------------------------------------------------------------
// MORPH_TIER — prestige rarity tier on the RARITY scale (Common..Legendary).
// Drives renown weight and discovery-animation flair.
// ---------------------------------------------------------------------------

export const MORPH_TIER: readonly Rarity[] = [
  RARITY.Rare,       // 0  — Toxic Bomber
  RARITY.Common,     // 1  — Sprintdrone
  RARITY.Uncommon,   // 2  — Bulwark Sentinel
  RARITY.Uncommon,   // 3  — Venom Lancer
  RARITY.Rare,       // 4  — Phantom Skimmer
  RARITY.Epic,       // 5  — Ironclad Colossus
  RARITY.Epic,       // 6  — Caustic Broodmother
  RARITY.Rare,       // 7  — Razorwing Reaper
  RARITY.Uncommon,   // 8  — Sentinel Augur
  RARITY.Rare,       // 9  — Quicksilver Stalker
  RARITY.Legendary,  // 10 — Molten Juggernaut
  RARITY.Legendary,  // 11 — Mirewing Leviathan
];

// ---------------------------------------------------------------------------
// MORPH_COUNT — the total number of discoverable morphs.
// ---------------------------------------------------------------------------

export const MORPH_COUNT = MORPH_NAME.length;

// ---------------------------------------------------------------------------
// Length assertions (evaluated at import time — catch mis-edits early).
// Pure data comparisons; no runtime cost in hot paths.
// ---------------------------------------------------------------------------

const _assertLengths = (() => {
  if (MORPH_DESC.length !== MORPH_COUNT)
    throw new Error(`MORPH_DESC length ${MORPH_DESC.length} !== MORPH_COUNT ${MORPH_COUNT}`);
  if (MORPH_REQ_TYPES.length !== MORPH_COUNT)
    throw new Error(`MORPH_REQ_TYPES length ${MORPH_REQ_TYPES.length} !== MORPH_COUNT ${MORPH_COUNT}`);
  if (MORPH_REQ_TRAITS.length !== MORPH_COUNT)
    throw new Error(`MORPH_REQ_TRAITS length ${MORPH_REQ_TRAITS.length} !== MORPH_COUNT ${MORPH_COUNT}`);
  if (MORPH_TIER.length !== MORPH_COUNT)
    throw new Error(`MORPH_TIER length ${MORPH_TIER.length} !== MORPH_COUNT ${MORPH_COUNT}`);

  // Every morph must combine at least 2 total requirements (type count + trait count).
  for (let i = 0; i < MORPH_COUNT; i++) {
    const total = (MORPH_REQ_TYPES[i] as readonly GeneType[]).length
                + (MORPH_REQ_TRAITS[i] as readonly GeneTrait[]).length;
    if (total < 2)
      throw new Error(`MORPH[${i}] "${MORPH_NAME[i]}" has only ${total} total requirement(s); each morph needs >= 2`);
  }
})();
